import { getApp, getApps, initializeApp } from 'firebase/app';
import { GoogleAuthProvider, getAuth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: 'AIzaSyC2club4pIDgrotuiDob_REi2qsC6zhd04',
  authDomain: 'student-recorder-web-app.firebaseapp.com',
  projectId: 'student-recorder-web-app',
  storageBucket: 'student-recorder-web-app.firebasestorage.app',
  messagingSenderId: '182472902284',
  appId: '1:182472902284:web:3ac56e54267b0ccdffda9',
  measurementId: 'G-3GFHDG3BKJ'
};

export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);

/**
 * Firestore's default WebChannel streaming is frequently blocked by proxies,
 * school networks and embedded frames, which surfaces as "unavailable" errors.
 * Auto-detecting long polling makes the connection survive those networks, and
 * a local cache keeps reads/writes working while the socket reconnects.
 */
function createFirestore(): Firestore {
  try {
    return initializeFirestore(app, {
      experimentalAutoDetectLongPolling: true,
      ignoreUndefinedProperties: true,
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
  } catch {
    try {
      return initializeFirestore(app, {
        experimentalAutoDetectLongPolling: true,
        ignoreUndefinedProperties: true,
        localCache: memoryLocalCache()
      });
    } catch {
      return getFirestore(app);
    }
  }
}

export const db = createFirestore();
export const storage = getStorage(app);

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

/** Firestore collection that holds every live student record. */
export const STUDENTS_COLLECTION = 'students';
/** Firestore collection that holds the per-account security profile. */
export const USERS_COLLECTION = 'users';
/** Firebase Storage folder for uploaded student photos. */
export const STUDENT_PHOTOS_PATH = 'student_photos';

/**
 * Google's OAuth popup cannot communicate with a page that is running inside an
 * iframe (preview panes, embeds). Detect it so we can offer a top-level tab.
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Error codes that mean "the popup route failed", not "the user said no". */
export const POPUP_FAILURE_CODES = [
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
  'auth/internal-error'
];

/** Turns raw Firebase error codes into human readable copy. */
export function describeFirebaseError(error: unknown): string {
  const code = typeof error === 'object' && error !== null ? String((error as { code?: string }).code ?? '') : '';
  const message =
    typeof error === 'object' && error !== null ? String((error as { message?: string }).message ?? '') : String(error);

  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The Google window closed before sign-in finished. This usually happens when the app runs inside a preview frame — open it in its own browser tab and try again.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in popup. Allow popups for this site and try again.';
    case 'auth/operation-not-supported-in-this-environment':
      return 'Google sign-in is not supported in this embedded environment. Open the app in its own browser tab.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorised in the Firebase project. Add it under Authentication → Settings → Authorized domains.';
    case 'auth/operation-not-allowed':
      return 'Google sign-in is not enabled yet. Turn on the Google provider in Firebase → Authentication → Sign-in method.';
    case 'auth/network-request-failed':
      return 'Network problem reaching Firebase. Check your connection and try again.';
    case 'permission-denied':
      return 'Firestore rejected the request. Check the security rules for the "students" collection.';
    case 'unavailable':
    case 'deadline-exceeded':
      return 'Firestore is not reachable from this network right now. Your work is kept on this device and syncs automatically once the connection returns.';
    case 'failed-precondition':
      return 'Firestore rejected the request. Make sure a Cloud Firestore database has been created for this project.';
    case 'unauthenticated':
      return 'Your session expired. Sign in with Google again to continue.';
    case 'storage/unauthorized':
      return 'Firebase Storage rejected the upload. Check the rules for "student_photos/".';
    case 'storage/retry-limit-exceeded':
      return 'Photo upload timed out. Try a smaller image.';
    default:
      return message || 'Something went wrong while talking to Firebase.';
  }
}
